import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Rocket, Orbit, Satellite, Zap, Radio, Globe, Maximize, Cpu } from 'lucide-react';

const SpaceArchitecture: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Orbital Institute";
    
    return (
        <div className="min-h-screen bg-[#050505] font-mono text-white selection:bg-cyan-500 selection:text-black border-x-[1px] border-white/10 mx-auto max-w-[1600px] relative overflow-hidden">
            
            {/* Starfield Background (CSS) */}
            <div className="absolute inset-0 z-0">
                <div className="w-full h-full bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-slate-900 via-[#050505] to-black"></div>
                <div className="absolute inset-0 opacity-20" style={{ 
                    backgroundImage: 'radial-gradient(white 1px, transparent 1px)', 
                    backgroundSize: '50px 50px' 
                }}></div>
            </div>

            {/* HUD Header */}
            <nav className="relative z-50 flex justify-between items-center p-6 border-b border-white/20 bg-black/50 backdrop-blur-md">
                <div className="flex items-center gap-4">
                    <div className="w-10 h-10 border border-cyan-500 flex items-center justify-center rounded-full animate-spin-slow">
                        <Orbit size={20} className="text-cyan-500" />
                    </div>
                    <div>
                        <div className="text-xs text-cyan-500 uppercase tracking-widest leading-none mb-1">Sector 7-G</div>
                        <h1 className="text-xl font-bold uppercase tracking-widest">{schoolName}</h1>
                    </div>
                </div>
                
                <div className="hidden md:flex gap-12 text-[10px] text-zinc-400 uppercase tracking-[0.2em] font-bold">
                    <a href="#" className="hover:text-cyan-400 transition-colors flex items-center gap-2"><Satellite size={12} /> Habitats</a>
                    <a href="#" className="hover:text-cyan-400 transition-colors flex items-center gap-2"><Rocket size={12} /> Propulsion</a>
                    <a href="#" className="hover:text-cyan-400 transition-colors flex items-center gap-2"><Cpu size={12} /> Systems</a>
                </div>

                <Button variant="outline" className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500 hover:text-black uppercase text-[10px] tracking-widest h-8 px-6 bg-transparent rounded-none">
                    Initialize
                </Button>
            </nav>

            {/* Hero Section */}
            <header className="relative z-10 grid grid-cols-1 lg:grid-cols-12 min-h-[80vh] border-b border-white/10">
                <div className="lg:col-span-4 p-12 flex flex-col justify-center border-r border-white/10">
                    <div className="text-cyan-500 text-xs mb-4 uppercase tracking-widest flex items-center gap-2">
                        <div className="w-2 h-2 bg-cyan-500 rounded-full animate-pulse"></div>
                        System Optimal
                    </div>
                    <h2 className="text-5xl md:text-7xl font-bold uppercase leading-none mb-8">
                        Design <br/>
                        For <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-600">Void.</span>
                    </h2>
                    <p className="text-zinc-500 text-sm leading-relaxed mb-12 max-w-sm">
                        Zero-gravity construction, psychological habitat design, and closed-loop life support engineering. 
                    </p>
                    <div className="flex flex-col gap-4">
                        <Button className="bg-white text-black hover:bg-cyan-400 hover:text-black font-bold uppercase tracking-widest text-xs h-12 w-full rounded-none">
                            Begin Simulation
                        </Button>
                        <div className="grid grid-cols-3 gap-2 text-[10px] uppercase text-zinc-600 text-center">
                            <div className="border border-zinc-800 p-2">O2: 98%</div>
                            <div className="border border-zinc-800 p-2">G: 0.0</div>
                            <div className="border border-zinc-800 p-2">PSI: 14.7</div>
                        </div>
                    </div>
                </div>

                {/* Main Visual */}
                <div className="lg:col-span-8 relative bg-black overflow-hidden group">
                    <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072&auto=format&fit=crop')] bg-cover bg-center opacity-60 group-hover:scale-105 transition-transform duration-[20s]"></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"></div>
                    
                    {/* SVG Blueprint Overlay */}
                    <div className="absolute inset-0 opacity-30 pointer-events-none">
                        <svg width="100%" height="100%">
                            <line x1="10%" y1="10%" x2="90%" y2="90%" stroke="cyan" strokeWidth="0.5" strokeDasharray="5,5" />
                            <circle cx="50%" cy="50%" r="20%" stroke="cyan" strokeWidth="0.5" fill="none" />
                            <rect x="80%" y="10%" width="100" height="50" stroke="cyan" strokeWidth="0.5" fill="none" />
                        </svg>
                    </div>

                    <div className="absolute bottom-12 left-12">
                        <h3 className="text-2xl font-bold uppercase tracking-widest mb-2">Kepler Station</h3>
                        <p className="text-cyan-500 text-xs uppercase tracking-widest">Low Earth Orbit • Class A</p>
                    </div>
                </div>
            </header>

            {/* Modules Grid */}
            <section className="relative z-10 grid grid-cols-2 md:grid-cols-4 bg-black">
                {[
                    { icon: Radio, label: "Comms", val: "Active" },
                    { icon: Zap, label: "Power", val: "Nuclear" },
                    { icon: Globe, label: "Terra", val: "Stage 1" },
                    { icon: Maximize, label: "Struct", val: "Tensile" },
                ].map((item, i) => (
                    <div key={i} className="border-r border-b border-white/10 p-8 flex flex-col items-center justify-center hover:bg-white/5 transition-colors cursor-pointer group">
                        <item.icon className="text-zinc-600 group-hover:text-cyan-400 mb-4 transition-colors" size={24} />
                        <span className="text-zinc-400 text-[10px] uppercase tracking-widest mb-1">{item.label}</span>
                        <span className="text-white font-bold uppercase text-xl">{item.val}</span>
                    </div>
                ))}
            </section>

             {/* Footer */}
            <footer className="relative z-10 border-t border-white/10 bg-black py-12 px-6 flex justify-between items-end">
                 <div className="text-[10px] text-zinc-600 uppercase tracking-widest">
                     <p>Orbital Mechanics Div.</p>
                     <p>© 2026 {schoolName}</p>
                 </div>
                 <div className="flex gap-2">
                     <div className="w-1 h-1 bg-cyan-500"></div>
                     <div className="w-1 h-1 bg-cyan-500/50"></div>
                     <div className="w-1 h-1 bg-cyan-500/20"></div>
                 </div>
            </footer>
        </div>
    );
};

export default SpaceArchitecture;
